const BASE_URL = process.env.REACT_APP_API_BASE_URL;
const SUPERADMIN_ROOT = `${BASE_URL}/internal/apis/v4/superadmin`;
const ROOT = `${SUPERADMIN_ROOT}/subscriptions/bulk-upload`;

// Terminal job states — polling stops here. `validated` and `invalid` are also
// resting states, but a validated job is waiting on the admin, not on the server.
export const TERMINAL_STATUSES = ["success", "partial", "failed", "invalid"];
export const RUNNING_STATUSES = ["queued", "running"];

const authHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem("authToken")}`,
});

// The backend wraps every payload as { msg: ... } and signals failure with a
// non-2xx, where msg carries the reason. One place to unwrap it.
const unwrap = async (response) => {
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    const detail = typeof data?.msg === "string" ? data.msg : JSON.stringify(data?.msg ?? data);
    throw new Error(detail || "Request failed");
  }
  return data.msg;
};

/**
 * Anchors for the picker.
 *
 * Deliberately the internal API, not eci's /v4/anchors/getall_anchors — that one
 * is gated to `internal_user`, so no wallet role can call it and the list would
 * come back empty for the very admins this page is for.
 */
export const getAnchors = async () => {
  const response = await fetch(`${SUPERADMIN_ROOT}/anchors/`, { headers: authHeaders() });
  return unwrap(response);
};

/** Validate a sheet. Creates a job but issues nothing. */
export const uploadBulkSubscriptions = async (anchorId, file) => {
  const body = new FormData();
  body.append("anchor_id", anchorId);
  body.append("file", file);
  const response = await fetch(`${ROOT}/`, { method: "POST", headers: authHeaders(), body });
  return unwrap(response);
};

/** Start issuing a validated job. Irreversible — every row is messaged. */
export const confirmBulkUpload = async (jobId) => {
  const response = await fetch(`${ROOT}/${jobId}/confirm/`, {
    method: "POST",
    headers: authHeaders(),
  });
  return unwrap(response);
};

export const getBulkUploadStatus = async (jobId) => {
  const response = await fetch(`${ROOT}/${jobId}/`, { headers: authHeaders() });
  return unwrap(response);
};

export const getBulkUploadHistory = async (anchorId) => {
  const query = anchorId ? `?anchor_id=${encodeURIComponent(anchorId)}` : "";
  const response = await fetch(`${ROOT}/history/${query}`, { headers: authHeaders() });
  return unwrap(response);
};

/** Downloads the blank sheet. Returns nothing — it triggers a browser download. */
export const downloadBulkTemplate = async () => {
  const response = await fetch(`${ROOT}/template/`, { headers: authHeaders() });
  if (!response.ok) {
    throw new Error("Could not download the template");
  }
  const blob = await response.blob();
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = "bulk_subscriptions_template.xlsx";
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};
